import { PageController } from '@client/components/_core/layout/PageController';
import { PublicNoIndex } from '@client/components/_shared/public/PublicNoIndex';
import { trpcOptions } from '@client/trpc/client';
import { useMutation } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { zodValidator } from '@tanstack/zod-adapter';
import { Alert, Card } from 'antd';
import { useEffect } from 'react';
import { match } from 'ts-pattern';
import { z } from 'zod';
import { INVITE_LINK_VALIDATION_MUTATION_META } from './invite-link-validation';
import { InvalidInvite } from './steps/InvalidInvite';
import { InviteDetails } from './steps/InviteDetails';
import { Loading } from './steps/Loading';
import { PasswordForm } from './steps/PasswordForm';
import { ProcessingSignUp } from './steps/ProcessingSignUp';
import { SignInMethodSelection } from './steps/SignInMethodSelection';
import { SignUpComplete } from './steps/SignUpComplete';
import { UserAlreadyExists } from './steps/UserAlreadyExists';
import type { Step } from './store';
import { useStore } from './store';

export const Route = createFileRoute('/invite')({
    validateSearch: zodValidator(
        z.object({
            inviteSecret: z.string(),
            email: z.string(),
        }),
    ),
    component: InviteSignUp,
});

function InviteSignUp() {
    const { inviteSecret, email } = Route.useSearch();
    const currentStep = useStore((state) => state.currentStep);
    const errorCode = useStore((state) => state.errorCode);
    const errorMessage = useStore((state) => state.errorMessage);
    const { setInviteSecret, setCurrentStep, setInviteData, setError, setAllowedAuthMethods, setAllowNameUpdate } =
        useStore((state) => state.actions);
    const { mutate: validateInviteLink } = useMutation(
        trpcOptions.user.validateInviteLink.mutationOptions({
            meta: INVITE_LINK_VALIDATION_MUTATION_META,
        }),
    );

    useEffect(() => {
        // Set loading state
        setCurrentStep('loading');

        validateInviteLink(
            { inviteSecret },
            {
                onSuccess: (data) => {
                    setInviteSecret(inviteSecret);
                    setAllowedAuthMethods(data.allowedAuthMethods || []);
                    setAllowNameUpdate(data.allowNameUpdate ?? true);
                    setInviteData({
                        email: email,
                        firstName: data.firstName || '',
                        lastName: data.lastName || '',
                        inviteSecret,
                    });
                    setCurrentStep('inviteDetails');
                },
                onError: (error) => {
                    match(error.data?.errorId)
                        .with('invite.linkNotFound', () => setCurrentStep('invalidInvite'))
                        .with('invite.linkExpired', () => setCurrentStep('invalidInvite'))
                        .with('invite.userAlreadyExists', () => setCurrentStep('userAlreadyExists'))
                        .otherwise(() => {
                            setError({
                                errorCode: 'unknown',
                                errorMessage: error.message,
                            });
                        });
                },
            },
        );
    }, [inviteSecret]);

    const renderStep = () => {
        return match(currentStep)
            .with('loading', () => <Loading />)
            .with('inviteDetails', () => <InviteDetails />)
            .with('signInMethodSelection', () => <SignInMethodSelection />)
            .with('passwordForm', () => <PasswordForm />)
            .with('processingSignUp', () => <ProcessingSignUp />)
            .with('signUpComplete', () => <SignUpComplete />)
            .with('invalidInvite', () => <InvalidInvite />)
            .with('userAlreadyExists', () => <UserAlreadyExists />)
            .exhaustive();
    };

    const formSteps: Step[] = ['signInMethodSelection', 'passwordForm'];
    const shouldWrapInCard = formSteps.includes(currentStep);

    const content = renderStep();

    return (
        <PageController route={Route}>
            <PublicNoIndex />
            {shouldWrapInCard ? (
                <div className="min-h-screen w-full bg-muted">
                    <div className="flex min-h-screen items-center justify-center">
                        <div className="w-full max-w-sm">
                            {errorCode ? (
                                <Alert title="Error" description={errorMessage} type="error" className="mx-4 mb-4" />
                            ) : null}
                            <Card className="mx-4 my-8">{content}</Card>
                        </div>
                    </div>
                </div>
            ) : (
                content
            )}
        </PageController>
    );
}
